import type { ProductReview } from '@eliadmin/core/src/api/products/types/product-types';
import { REVIEW_COPY } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-copy';

export const STARS = [5, 4, 3, 2, 1] as const;
export type Star = (typeof STARS)[number];

export type ReviewFilter = 'all' | 'photo' | Star;
export type ReviewSort = 'newest' | 'best' | 'worst';

export type ReviewStats = {
  count: number;
  /** Mean of the ratings (1–5). */
  average: number;
  distribution: Record<Star, number>;
  withPhoto: number;
};

const PLURAL = new Intl.PluralRules('sk');

/** API ratings are whole numbers 1–5; clamp so a bad value never breaks the distribution. */
export function ratingOf(review: ProductReview): Star {
  return Math.min(5, Math.max(1, Math.round(Number(review.rating) || 1))) as Star;
}

export function reviewStats(reviews: ProductReview[]): ReviewStats {
  const distribution: Record<Star, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  let sum = 0;
  let withPhoto = 0;
  for (const review of reviews) {
    const rating = ratingOf(review);
    distribution[rating] += 1;
    sum += rating;
    if (review.image) withPhoto += 1;
  }
  return {
    count: reviews.length,
    average: reviews.length ? sum / reviews.length : 0,
    distribution,
    withPhoto,
  };
}

const time = (review: ProductReview) => Date.parse(review.created_at) || 0;

const SORTERS: Record<ReviewSort, (a: ProductReview, b: ProductReview) => number> = {
  newest: (a, b) => time(b) - time(a),
  best: (a, b) => ratingOf(b) - ratingOf(a) || time(b) - time(a),
  worst: (a, b) => ratingOf(a) - ratingOf(b) || time(b) - time(a),
};

export function visibleReviews(reviews: ProductReview[], filter: ReviewFilter, sort: ReviewSort) {
  const matching = reviews.filter(review =>
    filter === 'all' ? true : filter === 'photo' ? !!review.image : ratingOf(review) === filter,
  );
  return matching.sort(SORTERS[sort]);
}

export function reviewAuthor(review: ProductReview) {
  return [review.name, review.surname].filter(Boolean).join(' ') || REVIEW_COPY.tab.anonymous;
}

/** „Marta K.“ → „MK“; empty when the author left no name. */
export function reviewInitials(review: ProductReview) {
  return [review.name, review.surname]
    .map(part => part?.match(/\p{L}/u)?.[0] ?? '')
    .join('')
    .toUpperCase();
}

/** „126 hodnotení“ with the Slovak plural (1 hodnotenie, 2–4 hodnotenia). */
export function reviewCountLabel(count: number) {
  const form = PLURAL.select(count);
  const word =
    form === 'one'
      ? REVIEW_COPY.count.one
      : form === 'few'
        ? REVIEW_COPY.count.few
        : REVIEW_COPY.count.other;
  return `${count} ${word}`;
}
